import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import images from "../../images";
import { OTPInput } from "../../components/OtpInputProps/OTPInputProps";
import {
  useVerifyOTPMutation,
  useLoginMutation,
  useVerifyOTPSignUpMutation,
  useResendOTPSignUpMutation,
} from "../../services/apiSlice";
import { toastUtils } from "../../Utils/helper";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import posthog from "posthog-js";
import { useDispatch } from "react-redux";
import { setHideWidget } from "../../redux/featuresSlice/commonSlice";

export default function WhatsappOTPPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const [otp, setOtp] = useState(Array(6).fill(""));
  const [resendCooldown, setResendCooldown] = useState(0);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [blockTime, setBlockTime] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);

  const phone = searchParams.get("phone");
  const countryCode = searchParams.get("country_code")?.replace(" ", "+") || "";
  const typeVerifyOTP = searchParams.get("type");
  const questId = searchParams.get("quest_id");
  const locationId = searchParams.get("location_id");
  const rememberMe = searchParams.get("remember_me");
  const fromNoti = searchParams.get("from");

  const [verifyOTP] = useVerifyOTPMutation();
  const [verifyOTPSignUp] = useVerifyOTPSignUpMutation();
  const [login] = useLoginMutation();
  const [resendOTPSignUp] = useResendOTPSignUpMutation();

  // Track otp_verification_page_viewed on page load
  useEffect(() => {
    posthog.capture("otp_verification_page_viewed", {
      verification_type: typeVerifyOTP,
      phone_country_code: countryCode,
      has_quest_context: !!questId && questId !== "null",
      has_location_context: !!locationId && locationId !== "null",
    });
  }, [typeVerifyOTP, countryCode, questId, locationId]);

  const getRedirectPath = () => {
    const buildUrlWithParams = (baseUrl, params = {}) => {
      const searchParams = new URLSearchParams(params);
      return searchParams.toString()
        ? `${baseUrl}?${searchParams.toString()}`
        : baseUrl;
    };

    if (fromNoti === "notifications") {
      return "/profile/notifications";
    }

    if (typeVerifyOTP === "signup") {
      const params = {};
      if (questId && questId !== "null") params.quest_id = questId;
      if (locationId && locationId !== "null") params.location_id = locationId;

      return buildUrlWithParams("/preview", params);
    }

    if (locationId && locationId !== "null") {
      const params = {};
      return buildUrlWithParams(`/quest/location/${locationId}`, params);
    }

    if (questId && questId !== "null") {
      const params = {};
      return buildUrlWithParams(`/quest/details/${questId}`, params);
    }

    return "/quest";
  };

  const getNextDestination = () => {
    if (fromNoti === "notifications") return "home";
    if (typeVerifyOTP === "signup") return "onboarding";
    if (questId && questId !== "null") return "quest_details";
    if (locationId && locationId !== "null") return "quest_location";
    return "home";
  };

  const handleSignIn = async () => {
    if (isBlocked) {
      toastUtils.error(t("whatAppOTP.blockedError"));
      return;
    }

    dispatch(setHideWidget(true));

    const otpValue = otp.join("");
    if (otpValue.length < 6) {
      posthog.capture("otp_verification_incomplete", {
        verification_type: typeVerifyOTP,
        otp_length: otpValue.length,
      });
      toastUtils.error(t("whatAppOTP.incompleteOTP"));
      return;
    }

    // Track otp_verification_started
    posthog.capture("otp_verification_started", {
      verification_type: typeVerifyOTP,
      phone_country_code: countryCode,
      failed_attempts: failedAttempts,
    });

    const body = {
      phone,
      country_code: countryCode,
      otp_code: otpValue,
    };

    const handleVerifyOTP = async (apiCall) => {
      try {
        const response = await apiCall(body).unwrap();
        const userId = response?.data?.id?.toString() || "";

        if (response.data.access_token && response.data.refresh_token) {
          const storage = rememberMe === "true" ? localStorage : sessionStorage;
          storage.setItem("accessToken", response.data.access_token);
          storage.setItem("refreshToken", response.data.refresh_token);
        }

        if (
          response.message === "Operation successful" ||
          response.status === 200
        ) {
          // Track otp_verification_success
          posthog.capture("otp_verification_success", {
            verification_type: typeVerifyOTP,
            phone_country_code: countryCode,
            failed_attempts: failedAttempts,
            user_id: userId,
            next_destination: getNextDestination(),
          });

          // Điều hướng dựa trên getRedirectPath
          navigate(getRedirectPath());
        }
      } catch (err) {
        const newFailedAttempts = failedAttempts + 1;
        setFailedAttempts(newFailedAttempts);

        // Track otp_verification_failed
        posthog.capture("otp_verification_failed", {
          verification_type: typeVerifyOTP,
          phone_country_code: countryCode,
          error_code: err?.status || 0,
          failed_attempts: newFailedAttempts,
          is_blocked: newFailedAttempts >= 5,
          error_message: err?.data?.message || t("toast.error.verifyOTPFailed"),
        });

        if (newFailedAttempts >= 5) {
          // Track otp_verification_blocked
          posthog.capture("otp_verification_blocked", {
            verification_type: typeVerifyOTP,
            failed_attempts: newFailedAttempts,
          });

          // Track otp_verification_account_blocked
          posthog.capture("otp_verification_account_blocked", {
            verification_type: typeVerifyOTP,
            phone_country_code: countryCode,
            total_failed_attempts: newFailedAttempts,
          });

          setIsBlocked(true);
          setBlockTime(30);
          toastUtils.error(t("whatAppOTP.blockedMessage"));
          setOtp(Array(6).fill(""));
        } else {
          toastUtils.error(
            t("toast.error.verifyOTPFailed", {
              attempts: 5 - newFailedAttempts,
            }),
          );
        }
      }
    };

    await handleVerifyOTP(
      typeVerifyOTP === "signup" ? verifyOTPSignUp : verifyOTP,
    );

    // Reset PostHog và cập nhật user info
    posthog.reset();
    const userInfoStr = localStorage.getItem("userInfo");
    if (userInfoStr) {
      try {
        const userInfo = JSON.parse(userInfoStr);
        const userId = userInfo?.data?.id;

        if (userId) {
          posthog.identify(userId.toString(), {
            userId: userId || "",
            phone_number: phone,
            account_status: "active",
            last_updated: new Date().toISOString(),
          });
          const posthogStorageKey = `ph_${process.env.REACT_APP_POSTHOG_KEY}_posthog`;
          const posthogData = localStorage.getItem(posthogStorageKey);
          if (posthogData) {
            const parsedData = JSON.parse(posthogData);
            if (parsedData) {
              parsedData.distinct_id = userId?.toString();
              localStorage.setItem(
                posthogStorageKey,
                JSON.stringify(parsedData),
              );
            }
          }
        }
      } catch (e) {
        console.error("Failed to parse userInfo from localStorage", e);
      }
    }
    setFailedAttempts(0);
    localStorage.setItem("isFirstLogin", "true");
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) {
      toastUtils.info(
        t("toast.info.resendOTPCooldown", { seconds: resendCooldown }),
      );
      return;
    }

    const body = { phone, country_code: countryCode };
    const apiCall = typeVerifyOTP === "signup" ? resendOTPSignUp : login;

    try {
      const response = await apiCall(body).unwrap();
      if (
        response.message === "Operation successful" ||
        response.status === 200
      ) {
        toastUtils.success(t("toast.success.resendOTPSuccess"));
        setResendCooldown(300);
        setFailedAttempts(0);
        setIsBlocked(false);
      }
    } catch (err) {
      toastUtils.error(t("toast.error.resendOTPFailed"));
    }
  };

  useEffect(() => {
    let timer;
    if (blockTime > 0) {
      timer = setInterval(() => {
        setBlockTime((prev) => {
          const newTime = prev - 1;
          if (newTime <= 0) {
            setIsBlocked(false);
            setFailedAttempts(0);
            return 0;
          }
          return newTime;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [blockTime]);

  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setInterval(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [resendCooldown]);

  return (
    <div className="section-h-full flex flex-col px-10 max-xs:py-9 py-[72px] max-xs:gap-[30px] gap-[60px] 2xs:max-w-[430px]">
      <Helmet>
        <meta name="theme-color" content="#ffffff" />
      </Helmet>
      <div className="flex items-center">
        <img src={images.LogoLogin} className="w-auto h-[60px]" alt="logo" />
      </div>
      <h1 className="text-4xl leading-none font-bold text-primary tracking-[-0.72px]">
        {t("whatAppOTP.enterWhatsAppOTP")}
      </h1>
      <div className="flex flex-col gap-6 justify-between w-full h-full bg-white">
        <div className="flex flex-col gap-[60px]">
          <div className="flex flex-col w-full text-sm leading-none text-mid-gray">
            <div className="flex flex-col gap-6">
              {isBlocked && (
                <div className="w-full py-3 px-4 bg-red-50 border border-red-100 rounded-lg">
                  <p className="text-destructive font-medium text-center">
                    {t("whatAppOTP.accountTemporarilyBlocked")}
                  </p>
                  <p className="text-sm text-red-500 text-center mt-1">
                    {t("whatAppOTP.blockedDescription", { time: blockTime })}
                  </p>
                </div>
              )}

              <OTPInput otp={otp} setOtp={setOtp} disabled={isBlocked} />

              {failedAttempts > 0 && !isBlocked && (
                <p className="text-amber-600 text-sm">
                  {t("whatAppOTP.attemptsRemaining", {
                    attempts: 5 - failedAttempts,
                  })}
                </p>
              )}
            </div>
          </div>
          <p className="font-medium [&>span]:font-[Figtree] [&>span]:text-sm leading-none w-full">
            <span className="text-mid-gray font-normal">
              {t("whatAppOTP.noRecieveOTP")}
            </span>{" "}
            <span
              onClick={handleResendOTP}
              className={`${
                resendCooldown > 0
                  ? "text-mid-gray"
                  : "text-primary cursor-pointer font-bold"
              }`}
            >
              {resendCooldown > 0
                ? `${t("whatAppOTP.resend")} (${resendCooldown}s)`
                : t("whatAppOTP.resend")}
            </span>
          </p>
        </div>
        <button
          onClick={handleSignIn}
          disabled={isBlocked}
          className={`rounded-md w-full h-[50px] text-[20px] font-medium leading-none  ${
            isBlocked
              ? "bg-light-grey text-mid-gray cursor-not-allowed"
              : "text-white bg-primary"
          }`}
        >
          {isBlocked ? t("whatAppOTP.pleaseWait") : t("base.login")}
        </button>
      </div>
    </div>
  );
}
